import { createRequire } from "node:module";
import { readFileSync } from "node:fs";
import { dirname, join } from "node:path";
//#region src/embeddedCode.ts
const isRecord$1 = (value) => typeof value === "object" && value !== null;
const findDefElem = (options, defname) => {
	if (!Array.isArray(options)) return null;
	for (const option of options) if (isRecord$1(option) && option["type"] === "DefElem" && option["defname"] === defname) return option;
	return null;
};
const readAsBody = (asDefElem) => {
	const arg = asDefElem["arg"];
	if (!isRecord$1(arg)) return null;
	const items = arg["items"];
	if (!Array.isArray(items) || items.length !== 1) return null;
	const first = items[0];
	if (!isRecord$1(first) || first["type"] !== "String") return null;
	const sval = first["sval"];
	if (typeof sval !== "string") return null;
	const asRange = asDefElem["range"];
	return {
		source: sval,
		startSearchFrom: Array.isArray(asRange) && typeof asRange[1] === "number" ? asRange[1] : 0
	};
};
const readLanguage = (langDefElem) => {
	if (!langDefElem) return null;
	const arg = langDefElem["arg"];
	if (!isRecord$1(arg) || arg["type"] !== "String") return null;
	const sval = arg["sval"];
	return typeof sval === "string" ? sval.toLowerCase() : null;
};
const findBodyToken = (tokens, startSearchFrom) => {
	for (const token of tokens) {
		if (token.type !== "String") continue;
		if (token.range[0] < startSearchFrom) continue;
		const raw = token.value;
		if (raw.startsWith("$")) {
			const tagEnd = raw.indexOf("$", 1);
			if (tagEnd === -1) continue;
			const tagLength = tagEnd + 1;
			return {
				innerStart: token.range[0] + tagLength,
				innerEnd: token.range[1] - tagLength,
				quoteStyle: "dollar"
			};
		}
		if (raw.startsWith("'")) return {
			innerStart: token.range[0] + 1,
			innerEnd: token.range[1] - 1,
			quoteStyle: "single"
		};
	}
	return null;
};
const buildLoc = (start, end, lineMap) => ({
	start: lineMap.getPosition(start),
	end: lineMap.getPosition(end)
});
const attachToFunctionStmt = (stmt, tokens, lineMap) => {
	const options = stmt["options"];
	const language = readLanguage(findDefElem(options, "language"));
	if (!language) return;
	const asDefElem = findDefElem(options, "as");
	if (!asDefElem) return;
	const body = readAsBody(asDefElem);
	if (!body) return;
	const tokenInfo = findBodyToken(tokens, body.startSearchFrom);
	if (!tokenInfo) return;
	const embedded = {
		type: "EmbeddedCode",
		language,
		source: body.source,
		quoteStyle: tokenInfo.quoteStyle,
		range: [tokenInfo.innerStart, tokenInfo.innerEnd],
		loc: buildLoc(tokenInfo.innerStart, tokenInfo.innerEnd, lineMap)
	};
	embedded.parent = stmt;
	stmt.embeddedCode = embedded;
};
const attachEmbeddedCode = (program, tokens, lineMap) => {
	for (const rawNode of program.body) {
		const node = rawNode;
		if (!isRecord$1(node)) continue;
		if (node["type"] !== "CreateFunctionStmt") continue;
		attachToFunctionStmt(node, tokens, lineMap);
	}
};
/**
* Collects every `EmbeddedCode` node in a parsed program. Intended for tools
* that need to enumerate PL function bodies — e.g. an ESLint processor that
* emits a virtual file per body, or a CI script that audits which languages
* are in use across a codebase.
*
* The returned list preserves source order so virtual-file indices line up
* with the order embedded bodies appear in the SQL file.
*/
const extractEmbeddedCode = (program) => {
	const result = [];
	for (const rawNode of program.body) {
		const node = rawNode;
		if (!isRecord$1(node)) continue;
		const embedded = node["embeddedCode"];
		if (isRecord$1(embedded) && embedded["type"] === "EmbeddedCode") result.push(embedded);
	}
	return result;
};
//#endregion
//#region src/utils.ts
const createByteToCharOffset = (code) => {
	let asciiOnly = true;
	for (let i = 0; i < code.length; i++) if (code.charCodeAt(i) > 127) {
		asciiOnly = false;
		break;
	}
	if (asciiOnly) return (byteOffset) => byteOffset;
	let byteLength = 0;
	for (let i = 0; i < code.length; i++) {
		const c = code.charCodeAt(i);
		if (c <= 127) byteLength += 1;
		else if (c <= 2047) byteLength += 2;
		else if (c >= 55296 && c <= 56319) {
			byteLength += 4;
			i++;
		} else byteLength += 3;
	}
	const map = new Int32Array(byteLength + 1);
	let bi = 0;
	for (let ci = 0; ci < code.length; ci++) {
		const c = code.charCodeAt(ci);
		let width;
		let charSpan = 1;
		if (c <= 127) width = 1;
		else if (c <= 2047) width = 2;
		else if (c >= 55296 && c <= 56319) {
			width = 4;
			charSpan = 2;
		} else width = 3;
		for (let k = 0; k < width; k++) map[bi + k] = ci;
		bi += width;
		if (charSpan === 2) ci++;
	}
	map[byteLength] = code.length;
	return (byteOffset) => {
		if (byteOffset < 0) return byteOffset;
		if (byteOffset >= map.length) return code.length;
		return map[byteOffset] ?? code.length;
	};
};
const createLineMap = (code) => {
	const lineStartOffsets = [0];
	for (let i = 0; i < code.length; i++) if (code[i] === "\n") lineStartOffsets.push(i + 1);
	const getPosition = (offset) => {
		let left = 0;
		let right = lineStartOffsets.length - 1;
		while (left < right) {
			const mid = Math.floor((left + right + 1) / 2);
			const midOffset = lineStartOffsets[mid];
			if (midOffset !== void 0 && midOffset <= offset) left = mid;
			else right = mid - 1;
		}
		const lineNumber = left + 1;
		const lineStartOffset = lineStartOffsets[left];
		return {
			line: lineNumber,
			column: lineStartOffset === void 0 ? 0 : offset - lineStartOffset
		};
	};
	return {
		lineStartOffsets,
		code,
		getPosition
	};
};
//#endregion
//#region src/manipulate.ts
const specialKeys = [
	"parent",
	"type",
	"range",
	"loc"
];
const isRecord = (value) => {
	return typeof value === "object" && value !== null;
};
const isArray = (value) => {
	return Array.isArray(value);
};
const createLocationFromPosition = (position, lineMap) => {
	const { line, column } = lineMap.getPosition(position);
	return {
		start: {
			position,
			line,
			column
		},
		end: {
			position,
			line,
			column
		}
	};
};
const setNodeLocation = (node, range, loc) => {
	node["range"] = range;
	node["loc"] = loc;
};
const addParents = (node, parent = null) => {
	node["parent"] = parent;
	for (const [key, value] of Object.entries(node)) {
		if (specialKeys.includes(key)) continue;
		if (Array.isArray(value)) {
			for (const item of value) if (isRecord(item) && "type" in item) addParents(item, node);
		} else if (isRecord(value) && "type" in value) addParents(value, node);
	}
};
const detectBareNodeType = (node) => {
	if (typeof node["aliasname"] === "string") return "Alias";
	return null;
};
const addTypes = (node) => {
	if (isArray(node)) {
		for (const item of node) if (isRecord(item)) addTypes(item);
	} else if (isRecord(node)) {
		const bareType = node["type"] == null ? detectBareNodeType(node) : null;
		if (bareType != null) node["type"] = bareType;
		else {
			const typeKey = Object.keys(node).find((k) => !specialKeys.includes(k) && isRecord(node[k]));
			if (typeKey) {
				node["type"] = typeKey;
				const value = node[typeKey];
				if (isRecord(value)) {
					delete node[typeKey];
					Object.assign(node, value);
				}
			}
		}
		for (const [key, value] of Object.entries(node)) if (!specialKeys.includes(key) && isRecord(value)) addTypes(value);
	}
};
const buildAddLocation = (locationMap, lineMap, byteToChar) => {
	const getParentLocation = (obj) => {
		const parent = obj["parent"];
		if (!isRecord(parent)) return null;
		if (parent["loc"] != null && parent["range"] != null) return {
			loc: parent["loc"],
			range: parent["range"]
		};
		return getParentLocation(parent);
	};
	const addLocation = (node) => {
		let minLocation = null;
		let maxLocation = null;
		const updateCurrentMinMax = (newMin, newMax) => {
			if (newMin == null || newMax == null) return;
			if (minLocation == null || newMin.start.position < minLocation.start.position) minLocation = newMin;
			if (maxLocation == null || newMax.end.position > maxLocation.end.position) maxLocation = newMax;
		};
		if (isArray(node)) {
			for (const item of node) if (isRecord(item)) {
				const { minLocation: childMin, maxLocation: childMax } = addLocation(item);
				updateCurrentMinMax(childMin, childMax);
			}
			return {
				minLocation,
				maxLocation
			};
		}
		if (!isRecord(node)) return {
			minLocation,
			maxLocation
		};
		for (const [key, value] of Object.entries(node)) if (isRecord(value) && !specialKeys.includes(key)) {
			const { minLocation: childMin, maxLocation: childMax } = addLocation(value);
			updateCurrentMinMax(childMin, childMax);
		}
		const rawLocation = typeof node["location"] === "number" && node["location"] >= 0 ? node["location"] : null;
		const location = rawLocation == null ? null : byteToChar(rawLocation);
		if (typeof node["location"] === "number") delete node["location"];
		if (location != null) {
			const locationInfo = locationMap[location];
			if (locationInfo) {
				const { start, end } = locationInfo;
				setNodeLocation(node, [start.position, end.position], {
					start: {
						line: start.line,
						column: start.column
					},
					end: {
						line: end.line,
						column: end.column
					}
				});
				updateCurrentMinMax(locationInfo, locationInfo);
			} else {
				const { line, column } = lineMap.getPosition(location);
				setNodeLocation(node, [location, location], {
					start: {
						line,
						column
					},
					end: {
						line,
						column
					}
				});
				const locationObj = createLocationFromPosition(location, lineMap);
				updateCurrentMinMax(locationObj, locationObj);
			}
		}
		if (node["range"] == null && node["loc"] == null && minLocation != null && maxLocation != null) setNodeLocation(node, [minLocation.start.position, maxLocation.end.position], {
			start: {
				line: minLocation.start.line,
				column: minLocation.start.column
			},
			end: {
				line: maxLocation.end.line,
				column: maxLocation.end.column
			}
		});
		if ("type" in node && (node["range"] == null || node["loc"] == null)) {
			const parentLocation = getParentLocation(node);
			if (parentLocation) {
				setNodeLocation(node, parentLocation.range, parentLocation.loc);
				const locationObj = {
					start: {
						position: parentLocation.range[0],
						line: parentLocation.loc.start.line,
						column: parentLocation.loc.start.column
					},
					end: {
						position: parentLocation.range[1],
						line: parentLocation.loc.end.line,
						column: parentLocation.loc.end.column
					}
				};
				updateCurrentMinMax(locationObj, locationObj);
			} else if (node["range"] == null || node["loc"] == null) setNodeLocation(node, [0, 0], {
				start: {
					line: 1,
					column: 0
				},
				end: {
					line: 1,
					column: 0
				}
			});
		}
		return {
			minLocation,
			maxLocation
		};
	};
	return addLocation;
};
const buildStartEndMap = (tokens) => {
	const result = {};
	for (const token of tokens) {
		const [start, end] = token.range;
		result[start] = {
			start: {
				position: start,
				line: token.loc.start.line,
				column: token.loc.start.column
			},
			end: {
				position: end,
				line: token.loc.end.line,
				column: token.loc.end.column
			}
		};
	}
	return result;
};
const isFallbackRange = (range) => isArray(range) && range.length === 2 && range[0] === 0 && range[1] === 0;
const repairFallbackLocations = (node, ancestorRange, ancestorLoc) => {
	if (isArray(node)) {
		for (const item of node) repairFallbackLocations(item, ancestorRange, ancestorLoc);
		return;
	}
	if (!isRecord(node)) return;
	let inheritedRange = ancestorRange;
	let inheritedLoc = ancestorLoc;
	if (isFallbackRange(node["range"])) setNodeLocation(node, [ancestorRange[0], ancestorRange[1]], {
		start: {
			line: ancestorLoc.start.line,
			column: ancestorLoc.start.column
		},
		end: {
			line: ancestorLoc.end.line,
			column: ancestorLoc.end.column
		}
	});
	else if (isArray(node["range"]) && isRecord(node["loc"]) && isRecord(node["loc"].start) && isRecord(node["loc"].end)) {
		inheritedRange = node["range"];
		inheritedLoc = node["loc"];
	}
	for (const [key, value] of Object.entries(node)) {
		if (specialKeys.includes(key)) continue;
		if (isRecord(value) || isArray(value)) repairFallbackLocations(value, inheritedRange, inheritedLoc);
	}
};
const isIdentifierLike = (token) => token.type === "Identifier" || token.type === "Keyword";
const unquoteIdentifier = (value) => {
	if (value.length >= 2 && value.startsWith("\"") && value.endsWith("\"")) return value.slice(1, -1).replace(/""/g, "\"");
	return value;
};
const findFirstTokenAtOrAfter = (tokens, position) => {
	let lo = 0;
	let hi = tokens.length;
	while (lo < hi) {
		const mid = lo + hi >>> 1;
		if (tokens[mid].range[0] >= position) hi = mid;
		else lo = mid + 1;
	}
	return lo;
};
const setRangeAndLoc = (node, range, lineMap) => {
	const startPos = lineMap.getPosition(range[0]);
	const endPos = lineMap.getPosition(range[1]);
	node["range"] = range;
	node["loc"] = {
		start: {
			line: startPos.line,
			column: startPos.column
		},
		end: {
			line: endPos.line,
			column: endPos.column
		}
	};
};
const resolveAliasNodeRanges = (alias, parentRange, tokens, lineMap) => {
	const aliasname = alias["aliasname"];
	if (typeof aliasname !== "string") return;
	const startIdx = findFirstTokenAtOrAfter(tokens, parentRange != null ? parentRange[0] : 0);
	const target = aliasname.toLowerCase();
	let aliasTokenIdx = -1;
	for (let i = startIdx; i < tokens.length; i++) {
		const token = tokens[i];
		if (!isIdentifierLike(token)) continue;
		if (parentRange != null && token.range[1] <= parentRange[1]) continue;
		if (unquoteIdentifier(token.value).toLowerCase() === target) {
			aliasTokenIdx = i;
			break;
		}
	}
	if (aliasTokenIdx === -1) return;
	const aliasToken = tokens[aliasTokenIdx];
	let endRange = aliasToken.range;
	const colnames = alias["colnames"];
	if (isArray(colnames) && colnames.length > 0) {
		const open = tokens[aliasTokenIdx + 1];
		if (open && open.type === "Punctuator" && open.value === "(") {
			const colTokens = [];
			let current = [];
			let depth = 1;
			let closeIdx = -1;
			for (let i = aliasTokenIdx + 2; i < tokens.length && depth > 0; i++) {
				const t = tokens[i];
				if (t.type === "Punctuator" && t.value === "(") {
					depth++;
					current.push(t);
				} else if (t.type === "Punctuator" && t.value === ")") {
					depth--;
					if (depth === 0) {
						closeIdx = i;
						colTokens.push(current);
						current = [];
					} else current.push(t);
				} else if (t.type === "Punctuator" && t.value === "," && depth === 1) {
					colTokens.push(current);
					current = [];
				} else current.push(t);
			}
			if (closeIdx !== -1) {
				endRange = tokens[closeIdx].range;
				for (let i = 0; i < colnames.length && i < colTokens.length; i++) {
					const colNode = colnames[i];
					const segment = colTokens[i];
					if (!isRecord(colNode) || segment.length === 0) continue;
					const first = segment[0];
					const last = segment[segment.length - 1];
					setRangeAndLoc(colNode, [first.range[0], last.range[1]], lineMap);
				}
			}
		}
	}
	setRangeAndLoc(alias, [aliasToken.range[0], endRange[1]], lineMap);
};
const resolveAliasRanges = (node, tokens, lineMap) => {
	if (isArray(node)) {
		for (const item of node) resolveAliasRanges(item, tokens, lineMap);
		return;
	}
	if (!isRecord(node)) return;
	const alias = node["alias"];
	if (isRecord(alias) && alias["type"] === "Alias") resolveAliasNodeRanges(alias, isArray(node["range"]) && typeof node["range"][0] === "number" && typeof node["range"][1] === "number" ? [node["range"][0], node["range"][1]] : null, tokens, lineMap);
	for (const [key, value] of Object.entries(node)) {
		if (specialKeys.includes(key)) continue;
		if (isRecord(value) || isArray(value)) resolveAliasRanges(value, tokens, lineMap);
	}
};
const manipulate = (pgAst, tokens, lineMap) => {
	const startEndMap = buildStartEndMap(tokens);
	const result = [];
	const byteToChar = createByteToCharOffset(lineMap.code);
	const addLocation = buildAddLocation(startEndMap, lineMap, byteToChar);
	for (const stmt of pgAst.stmts) {
		const stmtNode = structuredClone(stmt.stmt);
		addTypes(stmtNode);
		addParents(stmtNode);
		addLocation(stmtNode);
		const stmtLocation = typeof stmt.stmt_location === "number" ? stmt.stmt_location : 0;
		if (stmt.stmt_len > 0) {
			const startChar = byteToChar(stmtLocation);
			const endChar = byteToChar(stmtLocation + stmt.stmt_len);
			const startPos = lineMap.getPosition(startChar);
			const endPos = lineMap.getPosition(endChar);
			stmtNode["range"] = [startChar, endChar];
			stmtNode["loc"] = {
				start: {
					line: startPos.line,
					column: startPos.column
				},
				end: {
					line: endPos.line,
					column: endPos.column
				}
			};
		}
		const resolvedRange = stmtNode["range"];
		const resolvedLoc = stmtNode["loc"];
		if (isArray(resolvedRange) && isRecord(resolvedLoc)) repairFallbackLocations(stmtNode, resolvedRange, resolvedLoc);
		resolveAliasRanges(stmtNode, tokens, lineMap);
		result.push(stmtNode);
	}
	return result;
};
//#endregion
//#region src/pg-query-sync.ts
/**
* Synchronous WASM-based PostgreSQL query parser.
*
* This module loads the `@libpg-query/parser` WASM binary synchronously
* using `new WebAssembly.Module()` + `new WebAssembly.Instance()`,
* avoiding the library's async `loadModule()` requirement.
*
* This is necessary because ESLint parsers must be fully synchronous.
*/
const pkgDir = dirname(createRequire(import.meta.url).resolve("@libpg-query/parser"));
const wasmPath = join(pkgDir, "libpg-query.wasm");
const shimSource = readFileSync(join(pkgDir, "libpg-query.js"), "utf8");
const importLetterBySymbol = parseImportLetters(shimSource);
const exportLetterBySymbol = parseExportLetters(shimSource);
function parseImportLetters(src) {
	const match = src.match(/wasmImports\s*=\s*\{([^}]*)\}/);
	if (!match) throw new Error("Could not locate wasmImports in libpg-query.js");
	const out = {};
	for (const part of match[1].split(",")) {
		const m = part.match(/^\s*([A-Za-z]+)\s*:\s*([A-Za-z0-9_$]+)\s*$/);
		if (m) out[m[2]] = m[1];
	}
	return out;
}
function parseExportLetters(src) {
	const out = {};
	const assignRe = /([A-Za-z_$][A-Za-z0-9_$]*)\s*=\s*wasmExports\["([A-Za-z])"\]/g;
	for (let m; m = assignRe.exec(src);) out[m[1]] = m[2];
	const ctorMatch = src.match(/wasmExports\["([A-Za-z])"\]\s*\(\s*\)/);
	if (ctorMatch) out["__wasm_call_ctors"] = ctorMatch[1];
	return out;
}
function importLetter(symbol) {
	const letter = importLetterBySymbol[symbol];
	if (!letter) throw new Error(`Unknown libpg-query import symbol: ${symbol}`);
	return letter;
}
function exportLetter(symbol) {
	const letter = exportLetterBySymbol[symbol];
	if (!letter) throw new Error(`Unknown libpg-query export symbol: ${symbol}`);
	return letter;
}
let wasmMemory;
let HEAPU8;
let HEAPU32;
function updateMemoryViews() {
	const b = wasmMemory.buffer;
	HEAPU8 = new Uint8Array(b);
	HEAPU32 = new Uint32Array(b);
}
let wasmTable;
let setThrew;
let stackRestore;
let stackSave;
function getTableEntry(index) {
	return wasmTable.get(index);
}
const textDecoder = new TextDecoder();
function UTF8ToString(ptr) {
	if (!ptr) return "";
	let end = ptr;
	while (HEAPU8[end]) end++;
	return textDecoder.decode(HEAPU8.subarray(ptr, end));
}
function lengthBytesUTF8(str) {
	let len = 0;
	for (let i = 0; i < str.length; i++) {
		const c = str.charCodeAt(i);
		if (c <= 127) len++;
		else if (c <= 2047) len += 2;
		else if (c >= 55296 && c <= 57343) {
			len += 4;
			i++;
		} else len += 3;
	}
	return len;
}
function stringToUTF8(str, outPtr, maxBytes) {
	if (maxBytes <= 0) return;
	let outIdx = outPtr;
	const endIdx = outPtr + maxBytes - 1;
	for (let i = 0; i < str.length; i++) {
		let u = str.codePointAt(i);
		if (u <= 127) {
			if (outIdx >= endIdx) break;
			HEAPU8[outIdx++] = u;
		} else if (u <= 2047) {
			if (outIdx + 1 >= endIdx) break;
			HEAPU8[outIdx++] = 192 | u >> 6;
			HEAPU8[outIdx++] = 128 | u & 63;
		} else if (u <= 65535) {
			if (outIdx + 2 >= endIdx) break;
			HEAPU8[outIdx++] = 224 | u >> 12;
			HEAPU8[outIdx++] = 128 | u >> 6 & 63;
			HEAPU8[outIdx++] = 128 | u & 63;
		} else {
			if (outIdx + 3 >= endIdx) break;
			HEAPU8[outIdx++] = 240 | u >> 18;
			HEAPU8[outIdx++] = 128 | u >> 12 & 63;
			HEAPU8[outIdx++] = 128 | u >> 6 & 63;
			HEAPU8[outIdx++] = 128 | u & 63;
			i++;
		}
	}
	HEAPU8[outIdx] = 0;
}
function invoke_i(index) {
	const sp = stackSave();
	try {
		return getTableEntry(index)();
	} catch (e) {
		stackRestore(sp);
		if (e !== e + 0) throw e;
		setThrew(1, 0);
	}
}
function invoke_ii(index, a1) {
	const sp = stackSave();
	try {
		return getTableEntry(index)(a1);
	} catch (e) {
		stackRestore(sp);
		if (e !== e + 0) throw e;
		setThrew(1, 0);
	}
}
function invoke_iii(index, a1, a2) {
	const sp = stackSave();
	try {
		return getTableEntry(index)(a1, a2);
	} catch (e) {
		stackRestore(sp);
		if (e !== e + 0) throw e;
		setThrew(1, 0);
	}
}
function invoke_iiii(index, a1, a2, a3) {
	const sp = stackSave();
	try {
		return getTableEntry(index)(a1, a2, a3);
	} catch (e) {
		stackRestore(sp);
		if (e !== e + 0) throw e;
		setThrew(1, 0);
	}
}
function invoke_iiiii(index, a1, a2, a3, a4) {
	const sp = stackSave();
	try {
		return getTableEntry(index)(a1, a2, a3, a4);
	} catch (e) {
		stackRestore(sp);
		if (e !== e + 0) throw e;
		setThrew(1, 0);
	}
}
function invoke_iiiiii(index, a1, a2, a3, a4, a5) {
	const sp = stackSave();
	try {
		return getTableEntry(index)(a1, a2, a3, a4, a5);
	} catch (e) {
		stackRestore(sp);
		if (e !== e + 0) throw e;
		setThrew(1, 0);
	}
}
function invoke_v(index) {
	const sp = stackSave();
	try {
		getTableEntry(index)();
	} catch (e) {
		stackRestore(sp);
		if (e !== e + 0) throw e;
		setThrew(1, 0);
	}
}
function invoke_vi(index, a1) {
	const sp = stackSave();
	try {
		getTableEntry(index)(a1);
	} catch (e) {
		stackRestore(sp);
		if (e !== e + 0) throw e;
		setThrew(1, 0);
	}
}
function invoke_vii(index, a1, a2) {
	const sp = stackSave();
	try {
		getTableEntry(index)(a1, a2);
	} catch (e) {
		stackRestore(sp);
		if (e !== e + 0) throw e;
		setThrew(1, 0);
	}
}
function invoke_viii(index, a1, a2, a3) {
	const sp = stackSave();
	try {
		getTableEntry(index)(a1, a2, a3);
	} catch (e) {
		stackRestore(sp);
		if (e !== e + 0) throw e;
		setThrew(1, 0);
	}
}
function invoke_viiii(index, a1, a2, a3, a4) {
	const sp = stackSave();
	try {
		getTableEntry(index)(a1, a2, a3, a4);
	} catch (e) {
		stackRestore(sp);
		if (e !== e + 0) throw e;
		setThrew(1, 0);
	}
}
function invoke_ji(index, a1) {
	const sp = stackSave();
	try {
		return getTableEntry(index)(a1);
	} catch (e) {
		stackRestore(sp);
		if (e !== e + 0) throw e;
		setThrew(1, 0);
		return 0n;
	}
}
function assertFail(condition, filename, line, func) {
	throw new Error(`Assertion failed: ${UTF8ToString(condition)}, at: ${filename ? UTF8ToString(filename) : "unknown"}:${line} ${func ? UTF8ToString(func) : "unknown"}`);
}
function abortJs() {
	throw new Error("Aborted");
}
function emscriptenThrowLongjmp() {
	throw Infinity;
}
function emscriptenResizeHeap(requestedSize) {
	requestedSize >>>= 0;
	const maxHeapSize = 2147483648;
	if (requestedSize > maxHeapSize) return 0;
	const oldSize = HEAPU8.length;
	for (let cutDown = 1; cutDown <= 4; cutDown *= 2) {
		let over = oldSize * (1 + .2 / cutDown);
		over = Math.min(over, requestedSize + 100663296);
		const pages = (Math.min(maxHeapSize, Math.ceil(Math.max(requestedSize, over) / 65536) * 65536) - wasmMemory.buffer.byteLength + 65535) / 65536 | 0;
		try {
			wasmMemory.grow(pages);
			updateMemoryViews();
			return 1;
		} catch {}
	}
	return 0;
}
function exitFn(_code) {
	throw new Error(`exit(${String(_code)})`);
}
function fdClose() {
	return 0;
}
function fdRead(_fd, _iov, _iovcnt, pnum) {
	HEAPU32[pnum >>> 2] = 0;
	return 0;
}
function fdSeek(_fd, _offsetLow, _whence, _newOffset) {
	return 70;
}
function fdWrite(_fd, iov, iovcnt, pnum) {
	let written = 0;
	for (let i = 0; i < iovcnt; i++) {
		const len = HEAPU32[iov + 4 >>> 2];
		iov += 8;
		written += len;
	}
	HEAPU32[pnum >>> 2] = written;
	return 0;
}
const envImports = {};
const importBindings = [
	["invoke_i", invoke_i],
	["invoke_ii", invoke_ii],
	["invoke_iii", invoke_iii],
	["invoke_iiii", invoke_iiii],
	["invoke_iiiii", invoke_iiiii],
	["invoke_iiiiii", invoke_iiiiii],
	["invoke_v", invoke_v],
	["invoke_vi", invoke_vi],
	["invoke_vii", invoke_vii],
	["invoke_viii", invoke_viii],
	["invoke_viiii", invoke_viiii],
	["invoke_ji", invoke_ji],
	["___assert_fail", assertFail],
	["__abort_js", abortJs],
	["__emscripten_throw_longjmp", emscriptenThrowLongjmp],
	["_emscripten_resize_heap", emscriptenResizeHeap],
	["_exit", exitFn],
	["_fd_close", fdClose],
	["_fd_read", fdRead],
	["_fd_seek", fdSeek],
	["_fd_write", fdWrite]
];
for (const [symbol, fn] of importBindings) envImports[importLetter(symbol)] = fn;
const wasmImports = { a: envImports };
const wasmBinary = readFileSync(wasmPath);
const wasmModule = new WebAssembly.Module(wasmBinary);
const wasmExports = new WebAssembly.Instance(wasmModule, wasmImports).exports;
const getExport = (symbol) => wasmExports[exportLetter(symbol)];
wasmMemory = getExport("wasmMemory");
wasmTable = getExport("wasmTable");
updateMemoryViews();
const wasmParseQuery = getExport("_wasm_parse_query");
const wasmMalloc = getExport("_malloc");
const wasmFree = getExport("_free");
const wasmFreeString = getExport("_wasm_free_string");
setThrew = getExport("_setThrew");
stackRestore = getExport("__emscripten_stack_restore");
stackSave = getExport("_emscripten_stack_get_current");
getExport("__wasm_call_ctors")();
function stringToPtr(str) {
	const len = lengthBytesUTF8(str) + 1;
	const ptr = wasmMalloc(len);
	try {
		stringToUTF8(str, ptr, len);
		return ptr;
	} catch (error) {
		wasmFree(ptr);
		throw error;
	}
}
function parseSync(query) {
	const queryPtr = stringToPtr(query);
	let resultPtr = 0;
	try {
		resultPtr = wasmParseQuery(queryPtr);
		const resultStr = UTF8ToString(resultPtr);
		if (resultStr.startsWith("syntax error") || resultStr.startsWith("deparse error") || resultStr.startsWith("ERROR")) throw new Error(resultStr);
		return JSON.parse(resultStr);
	} finally {
		wasmFree(queryPtr);
		if (resultPtr) wasmFreeString(resultPtr);
	}
}
//#endregion
//#region src/tokenize.ts
const calculateLocationFromOffset = (lineMap, location, length = 1) => {
	const start = location;
	const end = location + length;
	return {
		range: [start, end],
		loc: {
			start: lineMap.getPosition(start),
			end: lineMap.getPosition(end)
		}
	};
};
const SQL_KEYWORDS_SET = new Set([
	"SELECT",
	"INSERT",
	"UPDATE",
	"DELETE",
	"CREATE",
	"ALTER",
	"DROP",
	"TABLE",
	"INDEX",
	"VIEW",
	"DATABASE",
	"SCHEMA",
	"COLUMN",
	"PRIMARY",
	"KEY",
	"FOREIGN",
	"REFERENCES",
	"CONSTRAINT",
	"UNIQUE",
	"CHECK",
	"DEFAULT",
	"AS",
	"BETWEEN",
	"CASE",
	"CAST",
	"EXISTS",
	"FALSE",
	"TRUE",
	"NOT",
	"NULL",
	"NULLS",
	"IS",
	"ISNULL",
	"NOTNULL",
	"AND",
	"OR",
	"ANY",
	"SOME",
	"IN",
	"LIKE",
	"ILIKE",
	"SIMILAR",
	"ESCAPE",
	"ASC",
	"DESC",
	"ORDER",
	"GROUP",
	"HAVING",
	"LIMIT",
	"OFFSET",
	"DISTINCT",
	"ALL",
	"EXCEPT",
	"INTERSECT",
	"UNION",
	"VALUES",
	"FROM",
	"INTO",
	"INTEGER",
	"INT",
	"BIGINT",
	"SMALLINT",
	"DEC",
	"DECIMAL",
	"NUMERIC",
	"REAL",
	"DOUBLE",
	"PRECISION",
	"FLOAT",
	"VARCHAR",
	"CHAR",
	"TEXT",
	"BOOLEAN",
	"DATE",
	"TIME",
	"TIMESTAMP",
	"INTERVAL",
	"UUID",
	"JSON",
	"JSONB",
	"ARRAY",
	"JOIN",
	"INNER",
	"LEFT",
	"RIGHT",
	"FULL",
	"OUTER",
	"CROSS",
	"NATURAL",
	"USING",
	"ON",
	"COUNT",
	"SUM",
	"AVG",
	"MIN",
	"MAX",
	"OVER",
	"PARTITION",
	"WINDOW",
	"RANGE",
	"ROWS",
	"UNBOUNDED",
	"PRECEDING",
	"FOLLOWING",
	"CURRENT",
	"ROW",
	"FUNCTION",
	"PROCEDURE",
	"RETURNS",
	"RETURN",
	"LANGUAGE",
	"PLPGSQL",
	"SQL",
	"IMMUTABLE",
	"STABLE",
	"VOLATILE",
	"SECURITY",
	"DEFINER",
	"INVOKER",
	"STRICT",
	"CALLED",
	"INPUT",
	"COST",
	"PARALLEL",
	"SAFE",
	"RESTRICTED",
	"UNSAFE",
	"DECLARE",
	"BEGIN",
	"END",
	"EXCEPTION",
	"WHEN",
	"RAISE",
	"NOTICE",
	"WARNING",
	"INFO",
	"LOG",
	"DEBUG",
	"EXECUTE",
	"PERFORM",
	"GET",
	"DIAGNOSTICS",
	"LOOP",
	"WHILE",
	"FOR",
	"FOREACH",
	"EXIT",
	"CONTINUE",
	"IF",
	"THEN",
	"ELSE",
	"ELSIF",
	"FOUND",
	"ROW_COUNT",
	"RESULT_OID",
	"PG_CONTEXT",
	"PG_DATATYPE_NAME",
	"PG_EXCEPTION_CONTEXT",
	"PG_EXCEPTION_DETAIL",
	"PG_EXCEPTION_HINT",
	"MESSAGE_TEXT",
	"RETURNED_SQLSTATE",
	"SCHEMA_NAME",
	"TABLE_NAME",
	"COLUMN_NAME",
	"CONSTRAINT_NAME",
	"PG_TYPE_NAME",
	"CALL",
	"DO",
	"BLOCK",
	"COMMIT",
	"ROLLBACK",
	"SAVEPOINT",
	"RELEASE",
	"START",
	"TRANSACTION",
	"WORK",
	"GRANT",
	"REVOKE",
	"PRIVILEGES",
	"USAGE",
	"CONNECT",
	"TEMPORARY",
	"TEMP",
	"TRIGGER",
	"RULE",
	"EVENT",
	"COMMENT",
	"ANALYSE",
	"ANALYZE",
	"AUTHORIZATION",
	"BINARY",
	"BOTH",
	"BY",
	"COLLATE",
	"COLLATION",
	"CONCURRENTLY",
	"CURRENT_CATALOG",
	"CURRENT_DATE",
	"CURRENT_ROLE",
	"CURRENT_SCHEMA",
	"CURRENT_TIME",
	"CURRENT_TIMESTAMP",
	"CURRENT_USER",
	"DEALLOCATE",
	"DEFERRABLE",
	"DEFERRED",
	"FETCH",
	"FREEZE",
	"LOCALTIME",
	"LOCALTIMESTAMP",
	"ONLY",
	"OVERLAPS",
	"PLACING",
	"SESSION_USER",
	"SET",
	"SYMMETRIC",
	"SYSTEM_USER",
	"TABLESAMPLE",
	"TO",
	"TRAILING",
	"USER",
	"VERBOSE",
	"WHERE",
	"WITH"
]);
const NUMERIC_PATTERN = /^\d+(\.\d+)?([eE][+-]?\d+)?$/;
const isKeyword = (value) => SQL_KEYWORDS_SET.has(value.toUpperCase());
const tokenizeSQL = (code) => {
	const lineMap = createLineMap(code);
	const tokens = [];
	const comments = [];
	let i = 0;
	const length = code.length;
	while (i < length) {
		const char = code[i];
		if (!char) break;
		if (/\s/.test(char)) {
			i++;
			continue;
		}
		if (char === "-" && i + 1 < length && code[i + 1] === "-") {
			const start = i;
			while (i < length && code[i] !== "\n") i++;
			const rawValue = code.slice(start, i);
			const { range, loc } = calculateLocationFromOffset(lineMap, start, rawValue.length);
			comments.push({
				type: "Line",
				value: rawValue.slice(2),
				range,
				loc
			});
			continue;
		}
		if (char === "/" && i + 1 < length && code[i + 1] === "*") {
			const start = i;
			i += 2;
			while (i < length - 1 && !(code[i] === "*" && code[i + 1] === "/")) i++;
			i += 2;
			const rawValue = code.slice(start, i);
			const { range, loc } = calculateLocationFromOffset(lineMap, start, rawValue.length);
			comments.push({
				type: "Block",
				value: rawValue.slice(2, -2),
				range,
				loc
			});
			continue;
		}
		if (char === "'" || char === "\"") {
			const quote = char;
			const start = i;
			i++;
			while (i < length) {
				const currentChar = code[i];
				if (!currentChar) break;
				if (currentChar === quote) if (i + 1 < length && code[i + 1] === quote) i += 2;
				else {
					i++;
					break;
				}
				else if (currentChar === "\\") i += 2;
				else i++;
			}
			const value = code.slice(start, i);
			const { range, loc } = calculateLocationFromOffset(lineMap, start, value.length);
			tokens.push({
				type: "String",
				value,
				range,
				loc
			});
			continue;
		}
		if (char === "$") {
			let j = i + 1;
			if (j < length && code[j] && /[a-zA-Z_]/.test(code[j])) {
				j++;
				while (j < length && code[j] && /[a-zA-Z0-9_]/.test(code[j])) j++;
			}
			if (j < length && code[j] === "$") {
				const tag = code.slice(i, j + 1);
				const start = i;
				i = j + 1;
				while (i < length) {
					if (code[i] === "$" && code.slice(i, i + tag.length) === tag) {
						i += tag.length;
						break;
					}
					i++;
				}
				const value = code.slice(start, i);
				const { range, loc } = calculateLocationFromOffset(lineMap, start, value.length);
				tokens.push({
					type: "String",
					value,
					range,
					loc
				});
				continue;
			}
		}
		if (/[(),;.=<>!+\-*/%|&:]/.test(char)) {
			const start = i;
			if (i < length - 1) {
				const twoChar = code.slice(i, i + 2);
				if ([
					"<=",
					">=",
					"<>",
					"!=",
					"::",
					"||",
					"&&"
				].includes(twoChar)) {
					i += 2;
					const { range, loc } = calculateLocationFromOffset(lineMap, start, 2);
					tokens.push({
						type: "Operator",
						value: twoChar,
						range,
						loc
					});
					continue;
				}
			}
			i++;
			const value = char;
			const { range, loc } = calculateLocationFromOffset(lineMap, start, 1);
			const type = /[(),;.]/.test(char) ? "Punctuator" : "Operator";
			tokens.push({
				type,
				value,
				range,
				loc
			});
			continue;
		}
		if (/[a-zA-Z_0-9]/.test(char)) {
			const start = i;
			let type;
			if (/\d/.test(char)) {
				while (i < length && code[i] && /[\d.]/.test(code[i])) i++;
				if (i < length && code[i] && /[eE]/.test(code[i])) {
					i++;
					if (i < length && code[i] && /[+-]/.test(code[i])) i++;
					while (i < length && code[i] && /\d/.test(code[i])) i++;
				}
				type = NUMERIC_PATTERN.test(code.slice(start, i)) ? "Numeric" : "Identifier";
			} else {
				while (i < length && code[i] && /[a-zA-Z0-9_]/.test(code[i])) i++;
				type = isKeyword(code.slice(start, i)) ? "Keyword" : "Identifier";
			}
			const value = code.slice(start, i);
			const { range, loc } = calculateLocationFromOffset(lineMap, start, value.length);
			tokens.push({
				type,
				value,
				range,
				loc
			});
			continue;
		}
		i++;
	}
	return {
		tokens,
		comments
	};
};
//#endregion
//#region src/visitorKeys.ts
const SKIP_KEYS = new Set([
	"type",
	"range",
	"loc",
	"parent"
]);
const BASE_VISITOR_KEYS = {
	Program: [
		"body",
		"tokens",
		"comments"
	],
	SQLStatement: ["statement"],
	SQLParseError: [],
	SQLProcedure: [],
	EmbeddedCode: []
};
const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
const buildVisitorKeys = (ast) => {
	const visitorKeys = {};
	const visitedNodes = /* @__PURE__ */ new WeakSet();
	const traverseNode = (node) => {
		if (!isObject(node) || visitedNodes.has(node)) return;
		visitedNodes.add(node);
		const type = typeof node["type"] === "string" ? node["type"] : "Unknown";
		const childKeys = visitorKeys[type] ?? [];
		if (!(type in visitorKeys)) visitorKeys[type] = childKeys;
		const addKey = (key) => {
			if (!childKeys.includes(key)) childKeys.push(key);
		};
		for (const [key, value] of Object.entries(node)) {
			if (SKIP_KEYS.has(key)) continue;
			if (Array.isArray(value)) {
				const containsObject = value.some(isObject);
				if (containsObject || value.length > 0) addKey(key);
				if (containsObject) for (const item of value) traverseNode(item);
			} else if (isObject(value)) {
				addKey(key);
				traverseNode(value);
			}
		}
	};
	traverseNode(ast);
	return {
		...visitorKeys,
		...BASE_VISITOR_KEYS
	};
};
//#endregion
//#region src/parse.ts
const parseForESLint = (code) => {
	const lineMap = createLineMap(code);
	const { tokens, comments } = tokenizeSQL(code);
	const program = {
		type: "Program",
		range: [0, code.length],
		loc: {
			start: {
				line: 1,
				column: 0
			},
			end: lineMap.getPosition(code.length)
		},
		body: [],
		tokens,
		comments
	};
	let body;
	try {
		body = manipulate(parseSync(code), tokens, lineMap);
	} catch (err) {
		body = [{
			type: "SQLParseError",
			range: program.range,
			loc: program.loc,
			error: err instanceof Error ? err.message : "Unknown SQL parsing error",
			raw: code
		}];
	}
	program.body = body;
	for (const node of body) node.parent = program;
	attachEmbeddedCode(program, tokens, lineMap);
	return {
		ast: program,
		visitorKeys: buildVisitorKeys(program),
		scopeManager: null
	};
};
const parse = (code) => {
	return parseForESLint(code).ast;
};
//#endregion
export { parseForESLint as n, extractEmbeddedCode as r, parse as t };

//# sourceMappingURL=parse-BrcLn6sq.js.map